import {
  Bell,
  Bookmark,
  ChevronDown,
  Gauge,
  HelpCircle,
  LogOut,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  ShieldCheck,
  Sun,
  Trophy
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { useAuth } from "../auth";
import { BrandLockup } from "./Brand";
import { cn } from "../lib/cn";
import { SPRING_FLUID, SPRING_SNAPPY } from "../lib/motion";
import { useTheme } from "../theme";
import { Button, Menu, MenuContent, MenuItem, MenuTrigger } from "./ui";

const navigation = [
  { to: "/dashboard", label: "Tổng quan", icon: Gauge },
  { to: "/saved", label: "Đã lưu", icon: Bookmark },
  { to: "/results", label: "Kết quả", icon: Trophy },
  { to: "/settings", label: "Cài đặt", icon: Settings }
] as const;

const pageMeta: Record<string, { title: string; eyebrow: string }> = {
  "/dashboard": { title: "Tổng quan", eyebrow: "Trạm quan sát" },
  "/saved": { title: "Bộ số đã lưu", eyebrow: "Thư viện tín hiệu" },
  "/results": { title: "Kết quả dự đoán", eyebrow: "Đối chiếu dữ liệu" },
  "/settings": { title: "Cài đặt", eyebrow: "Tài khoản & thiết bị" }
};

function Logo({ showName = false }: { showName?: boolean }) {
  return (
    <NavLink to="/dashboard" aria-label="LottoLab VN — về tổng quan" className="flex min-h-11 min-w-0 items-center rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <BrandLockup showName={showName} />
    </NavLink>
  );
}

function ThemeButton() {
  const { theme, toggleTheme } = useTheme();
  return (
    <Button
      variant="ghost"
      size="icon"
      className="rounded-2xl overflow-hidden"
      onClick={toggleTheme}
      aria-label={theme === "dark" ? "Chuyển sang giao diện sáng" : "Chuyển sang giao diện tối"}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={theme}
          initial={{ rotate: -90, scale: 0.7, opacity: 0 }}
          animate={{ rotate: 0, scale: 1, opacity: 1 }}
          exit={{ rotate: 90, scale: 0.7, opacity: 0 }}
          transition={SPRING_SNAPPY}
        >
          {theme === "dark" ? <Sun className="size-[18px]" /> : <Moon className="size-[18px]" />}
        </motion.div>
      </AnimatePresence>
    </Button>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { session, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const meta = pageMeta[location.pathname] ?? pageMeta["/dashboard"]!;
  const displayName = session?.user.displayName ?? session?.user.email ?? "Thành viên";
  const [railCollapsed, setRailCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("lottolab-rail-collapsed") === "true";
  });

  // Trạng thái thu gọn chỉ là tùy chọn cục bộ, không làm thay đổi hồ sơ người dùng.
  const toggleRail = () => {
    setRailCollapsed((current) => {
      const next = !current;
      window.localStorage.setItem("lottolab-rail-collapsed", String(next));
      return next;
    });
  };

  return (
    <div className="min-h-screen">
      <a
        href="#main-content"
        className="sr-only z-[100] rounded-xl bg-primary px-4 py-2 font-bold text-primary-foreground focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        Chuyển đến nội dung chính
      </a>

      <aside
        className={cn(
          "observatory-rail fixed inset-y-0 left-0 z-40 hidden border-r border-border/70 py-5 lg:flex lg:flex-col",
          railCollapsed ? "w-[76px] px-3" : "w-[240px] px-4"
        )}
      >
        <Logo showName={!railCollapsed} />
        <nav className="mt-10 flex w-full flex-col gap-2" aria-label="Điều hướng chính">
          {navigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              title={item.label}
              className={({ isActive }) =>
                cn(
                  "group relative flex min-h-12 w-full items-center gap-3 rounded-xl text-sm font-extrabold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  railCollapsed ? "justify-center px-0" : "px-3.5",
                  isActive
                    ? "text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive ? (
                    <motion.div
                      layoutId="desktopNavActiveIndicator"
                      className="absolute inset-0 rounded-xl bg-primary shadow-[0_12px_24px_-12px_hsl(var(--primary))]"
                      transition={SPRING_FLUID}
                    />
                  ) : null}
                  <span className="relative z-10 flex items-center gap-3">
                    <item.icon className="size-[18px]" />
                    <span className={railCollapsed ? "sr-only" : undefined}>{item.label}</span>
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto flex w-full flex-col items-center gap-2">
          <NavLink
            to="/responsible-play"
            className="flex size-11 items-center justify-center rounded-2xl text-muted-foreground transition-colors hover:bg-muted hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Chơi có trách nhiệm"
            title="Chơi có trách nhiệm"
          >
            <ShieldCheck className="size-[18px]" />
          </NavLink>
          <ThemeButton />
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl"
            onClick={toggleRail}
            aria-label={railCollapsed ? "Mở rộng thanh điều hướng" : "Thu gọn thanh điều hướng"}
          >
            {railCollapsed ? <PanelLeftOpen className="size-[18px]" /> : <PanelLeftClose className="size-[18px]" />}
          </Button>
        </div>
      </aside>

      <div className={railCollapsed ? "lg:pl-[76px]" : "lg:pl-[240px]"}>
        <header className="sticky top-0 z-30 border-b border-border/65 bg-background/78 backdrop-blur-2xl">
          <div className="mx-auto flex h-[72px] max-w-[1560px] items-center gap-3 px-4 md:px-7 xl:px-9">
            <div className="lg:hidden">
              <Logo showName />
            </div>
            <div className="hidden min-w-0 lg:block">
              <span className="block text-[9px] font-black uppercase tracking-[0.2em] text-primary">
                {meta.eyebrow}
              </span>
              <strong className="mt-0.5 block truncate text-sm font-extrabold">{meta.title}</strong>
            </div>

            <div className="ml-auto flex items-center gap-1.5">
              <div className="hidden lg:block">
                <ThemeButton />
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="relative rounded-2xl"
                aria-label="Cài đặt thông báo"
                onClick={() => navigate("/settings")}
              >
                <Bell className="size-[18px]" />
                <span className="absolute right-2.5 top-2.5 size-1.5 rounded-full bg-accent ring-2 ring-background" />
              </Button>
              <Menu>
                <MenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 rounded-2xl p-1 pr-2">
                    <span className="flex size-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-xs font-black text-white shadow-sm">
                      {displayName.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="hidden max-w-40 truncate text-sm sm:block">{displayName}</span>
                    <ChevronDown className="size-3.5 text-muted-foreground" />
                  </Button>
                </MenuTrigger>
                <MenuContent>
                  <div className="border-b border-border/70 px-3 py-2.5">
                    <span className="block text-[10px] font-bold text-muted-foreground">Đang đăng nhập</span>
                    <strong className="mt-0.5 block max-w-52 truncate text-xs">{session?.user.email}</strong>
                  </div>
                  <MenuItem asChild>
                    <NavLink to="/settings"><Settings className="size-4" /> Cài đặt tài khoản</NavLink>
                  </MenuItem>
                  <MenuItem asChild>
                    <NavLink to="/responsible-play"><HelpCircle className="size-4" /> Chơi có trách nhiệm</NavLink>
                  </MenuItem>
                  <MenuItem className="text-destructive" onSelect={() => void logout()}>
                    <LogOut className="size-4" /> Đăng xuất
                  </MenuItem>
                </MenuContent>
              </Menu>
            </div>
          </div>
        </header>

        <main
          id="main-content"
          className="mobile-safe-bottom page-enter mx-auto w-full max-w-[1560px] px-4 py-6 md:px-7 md:py-8 xl:px-9 xl:py-10"
        >
          {children}
        </main>
      </div>

      <nav
        aria-label="Điều hướng chính trên di động"
        className="fixed inset-x-3 bottom-3 z-40 grid grid-cols-4 rounded-[22px] border border-border/75 bg-card/90 p-1.5 shadow-[0_24px_60px_-24px_hsl(230_50%_8%/0.65)] backdrop-blur-2xl lg:hidden"
      >
        {navigation.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "relative flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-[16px] text-[9px] font-extrabold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isActive ? "text-primary-foreground" : "text-muted-foreground hover:bg-muted"
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive ? (
                  <motion.div
                    layoutId="mobileNavActiveIndicator"
                    className="absolute inset-0 rounded-[16px] bg-primary shadow-[0_8px_20px_-8px_hsl(var(--primary))]"
                    transition={SPRING_FLUID}
                  />
                ) : null}
                <span className="relative z-10 flex flex-col items-center justify-center gap-1">
                  <item.icon className="size-[18px]" />
                  <span>{item.label}</span>
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
